import { useMemo } from "react";

const IMAGE_TYPES = ["image", "imageMessage"];
const MIN_GROUP_SIZE = 4;
const MAX_INTERVAL_MS = 60000; // 60 segundos

// WuzAPI/API Oficial gravam placeholders no body quando a foto vem SEM legenda:
// "[image]"/"[imagem]"/"[vídeo]"... no envio e "📎 Mídia" no recebimento. Não é legenda real
// → deve agrupar igual ao Baileys.
const PLACEHOLDER_BODY =
  /^(?:\[(?:image|imagem|photo|foto|video|v[íi]deo|sticker|figurinha)\]|(?:📎\s*)?m[íi]dia)$/i;

/**
 * Hook que agrupa mensagens de imagem consecutivas do mesmo remetente.
 *
 * Regras:
 * - Somente imagens com mediaUrl válida
 * - Mesmo remetente (fromMe ou contact.id)
 * - 4+ consecutivas
 * - Intervalo máximo de 60s entre cada par
 * - Imagem com legenda (body ≠ filename) quebra grupo e renderiza individual
 * - Texto, audio, video, documento, sticker quebram grupo
 * - Mensagens apagadas não quebram, são ignoradas
 *
 * Retorna um Map<messageId, groupInfo> onde groupInfo = {
 *   groupId: string,           // ID do grupo (id da primeira msg)
 *   messages: message[],       // todas as mensagens do grupo
 *   isFirst: boolean,          // se é a primeira msg do grupo (renderiza o grid)
 *   isGrouped: boolean,        // se pertence a um grupo (ocultar render individual)
 * }
 */
const useImageGrouping = (messagesList, enabled = true) => {
  return useMemo(() => {
    const groupMap = new Map();

    if (!enabled || !messagesList || messagesList.length < MIN_GROUP_SIZE) {
      return groupMap;
    }

    // Identificar grupos de imagens consecutivas
    const groups = [];
    let currentGroup = [];

    const getBasename = (url) => {
      try { return decodeURIComponent(url.split("/").pop().split("?")[0]).trim(); }
      catch { return url.split("/").pop().split("?")[0].trim(); }
    };

    const hasCaption = (msg) => {
      if (!msg.body || !msg.body.trim()) return false;
      const body = msg.body.trim();
      // body igual ao nome do arquivo não é legenda real
      if (body === getBasename(msg.mediaUrl || "")) return false;
      // placeholder do WuzAPI/API Oficial ("[image]", "[imagem]", "[vídeo]"...) não é legenda real
      if (PLACEHOLDER_BODY.test(body)) return false;

      // WuzAPI/API Oficial gravam o body como NOME DE ARQUIVO quando a foto vem SEM legenda
      // (ex.: "image-123.jpg" ou um ID hex longo). Isso não é legenda real → deve agrupar igual ao Baileys.
      const looksLikeFilename =
        /^[^\s]+\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(body) ||
        /^[A-Fa-f0-9\-_]{16,}$/.test(body);
      if (looksLikeFilename) return false;
      return true;
    };

    const isImageMessage = (msg) => {
      if (!msg || msg.isDeleted) return false;
      if (!IMAGE_TYPES.includes(msg.mediaType) || !msg.mediaUrl) return false;
      // Imagem com legenda renderiza individual (não agrupa)
      if (hasCaption(msg)) return false;
      return true;
    };

    const getSender = (msg) => {
      if (msg.fromMe) return "__me__";
      return msg.contact?.id || msg.contact?.number || msg.contactId || "__unknown__";
    };

    const getTimestamp = (msg) => {
      return new Date(msg.createdAt).getTime();
    };

    for (let i = 0; i < messagesList.length; i++) {
      const msg = messagesList[i];

      // Pular mensagens apagadas — não quebram grupo
      if (msg.isDeleted) continue;

      if (isImageMessage(msg)) {
        if (currentGroup.length === 0) {
          currentGroup.push(msg);
        } else {
          const lastMsg = currentGroup[currentGroup.length - 1];
          const sameSender = getSender(msg) === getSender(lastMsg);
          const withinInterval = Math.abs(getTimestamp(msg) - getTimestamp(lastMsg)) <= MAX_INTERVAL_MS;

          if (sameSender && withinInterval) {
            currentGroup.push(msg);
          } else {
            // Remetente diferente ou intervalo > 60s: salvar grupo atual, iniciar novo
            if (currentGroup.length >= MIN_GROUP_SIZE) {
              groups.push([...currentGroup]);
            }
            currentGroup = [msg];
          }
        }
      } else {
        // Mensagem não-imagem: salvar grupo atual se >= 4
        if (currentGroup.length >= MIN_GROUP_SIZE) {
          groups.push([...currentGroup]);
        }
        currentGroup = [];
      }
    }

    // Último grupo pendente
    if (currentGroup.length >= MIN_GROUP_SIZE) {
      groups.push([...currentGroup]);
    }

    // Montar o Map
    for (const group of groups) {
      const groupId = `img-group-${group[0].id}`;
      for (let i = 0; i < group.length; i++) {
        groupMap.set(group[i].id, {
          groupId,
          messages: group,
          isFirst: i === 0,
          isGrouped: true,
        });
      }
    }

    return groupMap;
  }, [messagesList, enabled]);
};

export default useImageGrouping;
